import hoistNonReactStatics from 'hoist-non-react-statics';
import React, { useState, useLayoutEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import {
    getLanguage,
    getLocaleData,
    getLocaleMode,
    NuiIntlProviderLite,
} from '@concur/nui-intl-runtime';
import { isLocalizationModeEnabled, LOCALE_MODE_STEGANOGRAPHY } from '@concur/core-ui-shell';

export default (WrappedComponent) => {
    const Component = ({ testMessages, ...props }) => {
        const {
            lang: langProp = 'en',
        } = props;
        const [messages, setMessages] = useState(testMessages);

        // An explicit ?lang= in the URL takes precedence over the host-provided
        // lang prop, so floorplans honors the query string app-wide. The URL is
        // static for a mounted session (language changes navigate + remount), so
        // parse it once.
        const urlLang = useMemo(() => queryString.parse(window.location.search).lang, []);
        const lang = urlLang || langProp;

        const name = WrappedComponent.displayName || WrappedComponent.name;
        Component.displayName = name || 'withNuiIntl';

        const localeMode = getLocaleMode(lang);
        const langCode = getLanguage(localeMode
            ? document.querySelector('html')?.lang
            : lang);

        useLayoutEffect(() => {
            let didCancel = false;
            const fetchMessagesFile = async () => {
                let messagesFile;
                try {
                    messagesFile = await import(/* webpackChunkName: "translations/ui-floorplans-[request]" */ `../translations/${langCode}.json`);
                } catch (_e) {
                    messagesFile = await import(/* webpackChunkName: "translations/ui-floorplans-[request]" */ '../translations/en.json');
                }
                if (!didCancel) { // Ignore the file if the lang changes quickly
                    setMessages(messagesFile.default);
                }
            };
            if (!testMessages) {
                fetchMessagesFile();
            }
            return () => { didCancel = true; };
        }, [langCode]);

        // enable steganography if LocalizationMode cookie is set
        const nuiIntlProps = {
            localeData: {
                lang: langCode,
                locale: langCode,
                localeMode: isLocalizationModeEnabled()
                    ? LOCALE_MODE_STEGANOGRAPHY
                    : localeMode,
            },
        };

        const localeData = getLocaleData(nuiIntlProps);

        if (!messages) {
            return (
                <NuiIntlProviderLite
                    localeData={localeData}
                    customFormatters={{
                        // Use non-breaking space to retain the height of the text
                        // while the message bundle is loading
                        formattedMessage: () => <>&nbsp;</>,
                    }}
                >
                    <WrappedComponent {...props} lang={lang} />
                </NuiIntlProviderLite>
            );
        }

        return (
            <NuiIntlProviderLite messages={messages} localeData={localeData}>
                <WrappedComponent {...props} lang={lang} />
            </NuiIntlProviderLite>
        );
    };

    hoistNonReactStatics(Component, WrappedComponent);
    Component.propTypes = {
        lang: PropTypes.string,
        testMessages: PropTypes.object, // Only used for Jest tests
    };
    return Component;
};
